/**
 * useSmartDebts Hook
 * Routes between demo debts (anonymous) and cloud debts (authenticated) based on auth state
 * Drop-in replacement for useDataManager or useDebts
 */

import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useCloudDebts } from './useCloudDebts';
import { useDemoDebts } from './useDemoDebts';
import { analytics } from '../lib/posthog';

export const useSmartDebts = () => {
  const { user, authReady } = useAuth();
  const authLoading = !authReady;
  
  // Use cloud debt manager for authenticated users
  const cloudDebtManager = useCloudDebts();
  
  // Use demo debt manager for anonymous users
  const demoDebtManager = useDemoDebts();

  // Determine which manager to use
  const isAuthenticated = !!user;
  const isDemoMode = !isAuthenticated;

  // Track mode transitions
  const activeManager = useMemo(() => {
    if (authLoading) {
      // During auth loading, return a loading state
      return {
        debts: [],
        loading: true,
        error: null,
        isDemoMode: false,
        // Stub functions to prevent errors
        addDebt: () => {},
        updateDebt: () => {},
        deleteDebt: () => {},
        saveDebt: () => {},
        reorderDebts: () => {},
        loadDemoData: () => {},
        clearAllData: () => {}
      };
    }

    const manager = isDemoMode ? demoDebtManager : cloudDebtManager;
    
    // Track mode usage
    if (manager.debts?.length > 0) {
      analytics.track('debt_manager_active', {
        mode: isDemoMode ? 'demo' : 'cloud',
        debts_count: manager.debts.length,
        total_balance: manager.debts.reduce((sum, debt) => sum + (debt.balance || 0), 0),
        user_authenticated: isAuthenticated,
        timestamp: new Date().toISOString()
      });
    }

    return {
      ...manager,
      isDemoMode,
      isAuthenticated,
      mode: isDemoMode ? 'demo' : 'cloud'
    };
  }, [authLoading, isDemoMode, demoDebtManager, cloudDebtManager, isAuthenticated]);

  return activeManager;
};

// Convenience hook for components that need to know the mode
export const useDebtMode = () => {
  const { user } = useAuth();
  
  return {
    isDemoMode: !user,
    isAuthenticated: !!user,
    mode: !user ? 'demo' : 'cloud'
  };
};

export default useSmartDebts;